import { useEffect, useState } from "react";
import {
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  ExternalLink,
  Search,
  Filter,
  User,
  Clock,
  ShieldCheck,
  Eye,
  RotateCcw
} from "lucide-react";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { apiGet, apiSend } from "../../services/api";
import { formatDate } from "../../utils/date";
import type { Comment } from "../../types";

export function CommentsPage() {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);

  async function loadComments() {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGet<Comment[]>("/api/comments");
      setComments(res || []);
    } catch (err: any) {
      setError(err.message || "Failed to load reader comments");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadComments();
  }, []);

  async function handleStatusChange(id: number, status: "approved" | "rejected" | "spam") {
    try {
      setActionInProgress(id);
      await apiSend(`/api/comments/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status })
      });
      setComments((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status } : c))
      );
    } catch (err: any) {
      alert(err.message || "Failed to update comment status");
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Are you sure you want to delete this comment permanently?")) return;
    try {
      setActionInProgress(id);
      await apiSend(`/api/comments/${id}`, { method: "DELETE" });
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      alert(err.message || "Failed to delete comment");
    } finally {
      setActionInProgress(null);
    }
  }

  const filteredComments = comments.filter((c) => {
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      c.author_name.toLowerCase().includes(q) ||
      (c.author_email && c.author_email.toLowerCase().includes(q)) ||
      c.body.toLowerCase().includes(q) ||
      (c.article_title && c.article_title.toLowerCase().includes(q));
    return matchesStatus && matchesSearch;
  });

  const pendingCount = comments.filter((c) => c.status === "pending").length;
  const approvedCount = comments.filter((c) => c.status === "approved").length;
  const spamCount = comments.filter((c) => c.status === "spam").length;

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <MessageSquare size={13} className="text-[#C99A2E]" />
            <span>COMMUNITY ENGAGEMENT</span>
          </div>
          <h1 className="admin-title">Reader Discussions & Moderation</h1>
          <p className="admin-subtitle">
            Review, approve, or reject comments submitted by readers across all published magazine articles.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadComments}
            className="admin-btn-secondary"
          >
            Refresh Feed
          </button>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="admin-metric-card">
          <div className="flex items-center justify-between">
            <span className="admin-metric-label">Approved Comments</span>
            <CheckCircle size={18} className="text-emerald-600" />
          </div>
          <div className="admin-metric-value">{approvedCount}</div>
          <p className="admin-metric-note">Live on public articles</p>
        </div>

        <div className="admin-metric-card">
          <div className="flex items-center justify-between">
            <span className="admin-metric-label">Pending Moderation</span>
            <Clock size={18} className="text-amber-500" />
          </div>
          <div className="admin-metric-value">{pendingCount}</div>
          <p className="admin-metric-note">Awaiting editorial review</p>
        </div>

        <div className="admin-metric-card">
          <div className="flex items-center justify-between">
            <span className="admin-metric-label">Spam & Flagged</span>
            <AlertTriangle size={18} className="text-rose-500" />
          </div>
          <div className="admin-metric-value">{spamCount}</div>
          <p className="admin-metric-note">Quarantined submissions</p>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
          {(["all", "approved", "pending", "spam", "rejected"] as const).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                statusFilter === status
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, article..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
          />
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <LoadingState label="Loading reader discussions..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : filteredComments.length === 0 ? (
        <div className="admin-card-panel p-0 overflow-hidden">
          <EmptyState
            icon={MessageSquare}
            badge="READER ENGAGEMENT"
            title={
              statusFilter !== "all"
                ? `No ${statusFilter} comments found`
                : searchQuery
                ? "No matching reader comments"
                : "No reader comments yet"
            }
            description={
              searchQuery
                ? `No community feedback matches your search query "${searchQuery}". Try broadening your search or resetting filters.`
                : statusFilter !== "all"
                ? `There are currently zero reader submissions in the "${statusFilter}" moderation queue.`
                : "Reader discussions and feedback on published magazine articles will appear here for editorial moderation and review."
            }
            actionText={
              statusFilter !== "all" || searchQuery
                ? "Reset Moderation Filters"
                : "Refresh Discussions"
            }
            onAction={() => {
              setStatusFilter("all");
              setSearchQuery("");
              loadComments();
            }}
            actionIcon={RotateCcw}
          />
        </div>
      ) : (
        <div className="admin-card-panel p-0 overflow-hidden">
          <div className="admin-table-container">
            <table className="admin-table">
            <thead>
              <tr>
                <th>Author & Contact</th>
                <th>Comment Content</th>
                <th>Target Article</th>
                <th>Status</th>
                <th>Submitted</th>
                <th style={{ textAlign: "right" }}>Moderation Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredComments.map((comment) => (
                <tr key={comment.id}>
                  <td style={{ verticalAlign: "top" }}>
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                        {comment.author_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="font-bold text-slate-900 text-xs block">
                          {comment.author_name}
                        </span>
                        {comment.author_email && (
                          <span className="text-[11px] text-slate-500 block">
                            {comment.author_email}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  <td style={{ verticalAlign: "top" }}>
                    <div className="text-xs text-slate-800 leading-relaxed font-sans max-w-md">
                      {comment.body}
                    </div>
                  </td>

                  <td style={{ verticalAlign: "top" }}>
                    {comment.article_title ? (
                      <a
                        href={comment.article_slug ? `/article/${comment.article_slug}` : "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-slate-800 hover:text-[#C99A2E] flex items-center gap-1 max-w-xs truncate"
                      >
                        <span className="truncate">{comment.article_title}</span>
                        <ExternalLink size={12} className="shrink-0 text-slate-400" />
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">Article #{comment.article_id}</span>
                    )}
                  </td>

                  <td style={{ verticalAlign: "top" }}>
                    <span className={`status-pill status-${comment.status}`}>
                      <span className="status-dot" />
                      <span>{comment.status}</span>
                    </span>
                  </td>

                  <td style={{ verticalAlign: "top" }}>
                    <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
                      {formatDate(comment.created_at)}
                    </span>
                  </td>

                  <td style={{ verticalAlign: "top", textAlign: "right" }}>
                    <div className="admin-row-actions justify-end">
                      {comment.status !== "approved" && (
                        <button
                          type="button"
                          title="Approve Comment"
                          disabled={actionInProgress === comment.id}
                          onClick={() => handleStatusChange(comment.id, "approved")}
                          className="action-icon-btn text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          <CheckCircle size={15} />
                        </button>
                      )}

                      {comment.status !== "rejected" && (
                        <button
                          type="button"
                          title="Reject Comment"
                          disabled={actionInProgress === comment.id}
                          onClick={() => handleStatusChange(comment.id, "rejected")}
                          className="action-icon-btn text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                        >
                          <XCircle size={15} />
                        </button>
                      )}

                      {comment.status !== "spam" && (
                        <button
                          type="button"
                          title="Mark as Spam"
                          disabled={actionInProgress === comment.id}
                          onClick={() => handleStatusChange(comment.id, "spam")}
                          className="action-icon-btn text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                        >
                          <AlertTriangle size={15} />
                        </button>
                      )}

                      <button
                        type="button"
                        title="Delete Permanently"
                        disabled={actionInProgress === comment.id}
                        onClick={() => handleDelete(comment.id)}
                        className="action-icon-btn danger-btn"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
